import { useState, useEffect } from 'react';
import { fetchAnalyticsData, fetchDeliveredOrdersForReport, getDateRange, type AnalyticsData, type SalesSummary, type Order } from '@/lib/supabase';
import RevenueChart from './RevenueChart';

type Period = 'today' | 'week' | 'month' | 'allTime';

const PERIOD_TABS: { key: Period; label: string; icon: string }[] = [
  { key: 'today',   label: 'Today',    icon: 'ri-sun-line' },
  { key: 'week',    label: 'Week',     icon: 'ri-calendar-line' },
  { key: 'month',   label: 'Month',    icon: 'ri-calendar-2-line' },
  { key: 'allTime', label: 'All',      icon: 'ri-history-line' },
];

function StatCard({ label, value, sub, icon, color }: {
  label: string; value: string; sub?: string; icon: string; color: string;
}) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-gray-100">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color}`}>
        <i className={`${icon} text-white text-lg`}></i>
      </div>
      <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
      {sub && <p className="text-gray-400 text-xs mt-1">{sub}</p>}
      <p className="text-gray-500 text-xs mt-1 font-medium">{label}</p>
    </div>
  );
}

function SummarySection({ summary, period }: { summary: SalesSummary; period: Period }) {
  const periodLabel = PERIOD_TABS.find(t => t.key === period)?.label ?? '';
  return (
    <div className="grid grid-cols-3 gap-3 mb-5">
      <StatCard
        label={`Delivered Orders (${periodLabel})`}
        value={summary.totalOrders.toString()}
        icon="ri-check-double-line"
        color="bg-green-500"
      />
      <StatCard
        label={`Total Revenue (${periodLabel})`}
        value={`₹${summary.totalRevenue.toLocaleString('en-IN')}`}
        sub={summary.totalOrders > 0 ? `${summary.totalOrders} orders` : undefined}
        icon="ri-money-rupee-circle-line"
        color="bg-gray-900"
      />
      <StatCard
        label="Avg Order Value"
        value={`₹${summary.avgOrderValue.toLocaleString('en-IN')}`}
        icon="ri-bar-chart-line"
        color="bg-amber-500"
      />
    </div>
  );
}

// ── Sales Report Generator ────────────────────────────────────────────────────
function generateSalesReportHTML(orders: Order[], period: string, summary: SalesSummary): string {
  const now = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  const deliveredOrders = orders.filter(o => o.status === 'delivered');

  const rows = deliveredOrders.slice(0, 200).map(o => `
    <tr>
      <td style="padding:8px 10px;font-size:12px;color:#374151;border-bottom:1px solid #f3f4f6;">${o.id}</td>
      <td style="padding:8px 10px;font-size:12px;color:#374151;border-bottom:1px solid #f3f4f6;">${o.customer_name}</td>
      <td style="padding:8px 10px;font-size:12px;color:#374151;border-bottom:1px solid #f3f4f6;">${o.customer_phone}</td>
      <td style="padding:8px 10px;font-size:12px;color:#374151;border-bottom:1px solid #f3f4f6;">${o.items.map(i => i.name).join(', ')}</td>
      <td style="padding:8px 10px;font-size:12px;color:#374151;text-align:right;border-bottom:1px solid #f3f4f6;font-weight:600;">&#8377;${o.final_amount.toLocaleString('en-IN')}</td>
      <td style="padding:8px 10px;font-size:12px;color:#374151;border-bottom:1px solid #f3f4f6;">${new Date(o.created_at).toLocaleDateString('en-IN')}</td>
      <td style="padding:8px 10px;font-size:12px;border-bottom:1px solid #f3f4f6;"><span style="background:#dcfce7;color:#16a34a;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:600;">Delivered</span></td>
    </tr>`).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Sales Report - ClothyBazar - ${period}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Segoe UI',Arial,sans-serif;background:#f9fafb;padding:24px 16px;}
    .report{background:#fff;max-width:900px;margin:0 auto;border-radius:16px;overflow:hidden;border:1px solid #e5e7eb;}
    table{width:100%;border-collapse:collapse;}
    th{padding:10px;font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;text-align:left;border-bottom:2px solid #e5e7eb;background:#f9fafb;}
    @media print{body{background:#fff;padding:0;} .report{border:none;border-radius:0;} .no-print{display:none;}}
  </style>
</head>
<body>
<div class="report">
  <div style="background:linear-gradient(to right,#1f2937,#374151);padding:24px 28px;color:#fff;">
    <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;">
      <div style="display:flex;align-items:center;gap:12px;">
        <div style="width:44px;height:44px;background:rgba(255,255,255,0.15);border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:22px;">&#128248;</div>
        <div><div style="font-weight:800;font-size:18px;">ClothyBazar</div><div style="opacity:.7;font-size:12px;">Sales Report — ${period}</div></div>
      </div>
      <div style="text-align:right;"><div style="opacity:.7;font-size:11px;">Generated on</div><div style="font-weight:600;font-size:13px;">${now}</div></div>
    </div>
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-top:20px;">
      <div style="background:rgba(255,255,255,0.1);border-radius:12px;padding:14px;">
        <div style="opacity:.7;font-size:11px;margin-bottom:4px;">Total Orders</div>
        <div style="font-size:22px;font-weight:800;">${summary.totalOrders}</div>
      </div>
      <div style="background:rgba(255,255,255,0.1);border-radius:12px;padding:14px;">
        <div style="opacity:.7;font-size:11px;margin-bottom:4px;">Total Revenue</div>
        <div style="font-size:22px;font-weight:800;">&#8377;${summary.totalRevenue.toLocaleString('en-IN')}</div>
      </div>
      <div style="background:rgba(255,255,255,0.1);border-radius:12px;padding:14px;">
        <div style="opacity:.7;font-size:11px;margin-bottom:4px;">Avg Order Value</div>
        <div style="font-size:22px;font-weight:800;">&#8377;${summary.avgOrderValue.toLocaleString('en-IN')}</div>
      </div>
    </div>
  </div>
  <div style="padding:20px 28px;">
    <table>
      <thead><tr>
        <th>Order ID</th><th>Customer</th><th>Phone</th><th>Items</th><th style="text-align:right;">Amount</th><th>Date</th><th>Status</th>
      </tr></thead>
      <tbody>${rows || '<tr><td colspan="7" style="text-align:center;padding:24px;color:#9ca3af;">No delivered orders found for this period.</td></tr>'}</tbody>
    </table>
  </div>
  <div style="padding:14px 28px;background:#f9fafb;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;">
    <span style="font-size:11px;color:#9ca3af;">ClothyBazar — Dehradun, Uttarakhand</span>
    <span style="font-size:11px;color:#9ca3af;">Total ${deliveredOrders.length} delivered orders</span>
  </div>
</div>
</body></html>`;
}

export default function AnalyticsPanel() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<Period>('today');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const result = await fetchAnalyticsData();
    setData(result);
    setLastUpdated(new Date());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const getPeriodKey = (): 'today' | 'week' | 'month' | 'all' => {
    if (period === 'allTime') return 'all';
    return period as 'today' | 'week' | 'month';
  };

  const handleDownloadReport = async () => {
    setReportLoading(true);
    const { from, to } = getDateRange(getPeriodKey());
    const orders = await fetchDeliveredOrdersForReport(from, to);
    const summary: SalesSummary = data ? data[period] : { totalOrders: 0, totalRevenue: 0, avgOrderValue: 0 };
    const periodLabel = PERIOD_TABS.find(t => t.key === period)?.label ?? period;
    const html = generateSalesReportHTML(orders, periodLabel, summary);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `SalesReport-${periodLabel}-${new Date().toISOString().slice(0, 10)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    setReportLoading(false);
  };

  const handlePrintReport = async () => {
    setReportLoading(true);
    const { from, to } = getDateRange(getPeriodKey());
    const orders = await fetchDeliveredOrdersForReport(from, to);
    const summary: SalesSummary = data ? data[period] : { totalOrders: 0, totalRevenue: 0, avgOrderValue: 0 };
    const periodLabel = PERIOD_TABS.find(t => t.key === period)?.label ?? period;
    const html = generateSalesReportHTML(orders, periodLabel, summary);
    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); win.onload = () => win.print(); }
    setReportLoading(false);
  };

  const summary: SalesSummary = data
    ? data[period]
    : { totalOrders: 0, totalRevenue: 0, avgOrderValue: 0 };

  const maxQty = data && data.topProducts.length > 0 ? data.topProducts[0].totalQuantity : 1;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gray-900 flex items-center justify-center">
            <i className="ri-bar-chart-2-line text-white text-base"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-sm">Sales Analytics</p>
            {lastUpdated && (
              <p className="text-gray-400 text-xs">
                Updated: {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-100 text-gray-700 text-xs font-semibold hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Refresh
        </button>
      </div>

      {/* Sales Report Download/Print Buttons */}
      <div className="bg-gray-900 rounded-2xl p-4 mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <i className="ri-file-chart-line text-white text-lg"></i>
          </div>
          <div>
            <p className="text-white font-bold text-sm">Sales Report</p>
            <p className="text-gray-400 text-xs">
              {PERIOD_TABS.find(t => t.key === period)?.label} ka record download ya print karo
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handlePrintReport}
            disabled={loading || reportLoading}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className="ri-printer-line"></i>Print
          </button>
          <button
            onClick={handleDownloadReport}
            disabled={loading || reportLoading}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {reportLoading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-download-2-line"></i>}
            Download
          </button>
        </div>
      </div>

      {/* Period Tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 p-3 mb-5">
        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2 px-1">Period Select Karo</p>
        <div className="grid grid-cols-4 gap-1.5">
          {PERIOD_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setPeriod(tab.key)}
              className={`flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                period === tab.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-100'
              }`}
            >
              <i className={`${tab.icon} text-base`}></i>
              <span className="leading-none">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin"></div>
          <p className="text-gray-500 text-sm">Analytics load ho rahi hai...</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <SummarySection summary={summary} period={period} />

          {/* Revenue Chart */}
          <RevenueChart />

          {/* All Periods Quick View */}
          <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-5">
            <p className="text-gray-700 font-bold text-sm mb-3 flex items-center gap-2">
              <i className="ri-calendar-check-line text-gray-500"></i>
              Sabhi Periods Ka Overview
            </p>
            <div className="space-y-2">
              {PERIOD_TABS.map(tab => {
                const s = data ? data[tab.key] : { totalOrders: 0, totalRevenue: 0, avgOrderValue: 0 };
                const isActive = tab.key === period;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setPeriod(tab.key)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all cursor-pointer ${
                      isActive ? 'bg-gray-900 text-white' : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <i className={`${tab.icon} text-sm ${isActive ? 'text-white' : 'text-gray-500'}`}></i>
                      <span className={`text-xs font-semibold ${isActive ? 'text-white' : 'text-gray-700'}`}>{tab.label}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className={`text-xs font-bold ${isActive ? 'text-white' : 'text-gray-900'}`}>
                          {s.totalOrders} orders
                        </p>
                        <p className={`text-[10px] ${isActive ? 'text-white/70' : 'text-gray-400'}`}>delivered</p>
                      </div>
                      <div className="text-right min-w-[80px]">
                        <p className={`text-xs font-bold ${isActive ? 'text-green-300' : 'text-green-600'}`}>
                          ₹{s.totalRevenue.toLocaleString('en-IN')}
                        </p>
                        <p className={`text-[10px] ${isActive ? 'text-white/70' : 'text-gray-400'}`}>revenue</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Top Selling Products */}
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-700 font-bold text-sm flex items-center gap-2">
                <i className="ri-fire-line text-amber-500"></i>
                Most Selling Products
              </p>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">All Time</span>
            </div>

            {!data || data.topProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-gray-500 text-sm text-center">
                  Abhi tak koi delivered order nahi hai.<br />
                  <span className="text-gray-400 text-xs">Jab orders deliver honge, yahan products dikhenge.</span>
                </p>
              </div>
            ) : (
              <>
                {/* Table Header */}
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-3 pb-2 border-b border-gray-100 mb-1">
                  <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">Product</p>
                  <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider text-center w-10">Today</p>
                  <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider text-center w-10">Week</p>
                  <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider text-center w-14">Monthly</p>
                </div>

                <div className="space-y-2">
                  {data.topProducts.map((product, idx) => {
                    const barWidth = Math.round((product.totalQuantity / maxQty) * 100);
                    const rankColors = ['bg-amber-400', 'bg-gray-500', 'bg-amber-700', 'bg-gray-400', 'bg-gray-300'];
                    const rankColor = rankColors[idx] ?? 'bg-gray-200';
                    return (
                      <div key={product.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all">

                        {/* Product Info */}
                        <div className="flex items-center gap-2 min-w-0">
                          {/* Rank badge */}
                          <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${rankColor}`}>
                            <span className="text-white text-[10px] font-bold">#{idx + 1}</span>
                          </div>
                          {/* Image */}
                          <div className="w-10 h-13 rounded-lg overflow-hidden shrink-0 bg-gray-200" style={{ height: '52px' }}>
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          {/* Name + category + bar */}
                          <div className="min-w-0 flex-1">
                            <p className="text-gray-900 text-xs font-bold truncate leading-tight">{product.name}</p>
                            <p className="text-gray-400 text-[10px] truncate mb-1">{product.category}</p>
                            <div className="w-full bg-gray-200 rounded-full h-1">
                              <div
                                className="bg-gray-800 h-1 rounded-full"
                                style={{ width: `${barWidth}%` }}
                              ></div>
                            </div>
                            <p className="text-gray-400 text-[10px] mt-0.5">Total: {product.totalQuantity} sold · ₹{product.totalRevenue.toLocaleString('en-IN')}</p>
                          </div>
                        </div>

                        {/* Today */}
                        <div className="w-10 flex flex-col items-center">
                          <p className={`text-sm font-bold leading-none ${product.todayQty > 0 ? 'text-green-600' : 'text-gray-300'}`}>
                            {product.todayQty}
                          </p>
                          <p className="text-[9px] text-gray-400 mt-0.5">pcs</p>
                        </div>

                        {/* Week */}
                        <div className="w-10 flex flex-col items-center">
                          <p className={`text-sm font-bold leading-none ${product.weekQty > 0 ? 'text-amber-600' : 'text-gray-300'}`}>
                            {product.weekQty}
                          </p>
                          <p className="text-[9px] text-gray-400 mt-0.5">pcs</p>
                        </div>

                        {/* Monthly */}
                        <div className="w-14 flex flex-col items-center">
                          <p className={`text-sm font-bold leading-none ${product.monthQty > 0 ? 'text-gray-800' : 'text-gray-300'}`}>
                            {product.monthQty}
                          </p>
                          <p className="text-[9px] text-gray-400 mt-0.5">pcs</p>
                        </div>

                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
